import { AppBar, AppBarProps, Stack, Toolbar, Typography } from '@mui/material'
import { forwardRef, ReactNode } from 'react'
import { IconButton } from 'utoniq-ui'

import { styles, styleSets } from '@/components/styles'
import { useRouter } from '@/core'

export type HeaderProps = {
  back?: boolean
  defaultBackPage?: string
  title?: ReactNode
  leftTitle?: boolean
  right?: ReactNode
  left?: ReactNode
  type?: 'normal' | 'modal'
  color?: string
  bgcolor?: string
  responsive?: boolean
  maxWidth?: number
} & Omit<AppBarProps, 'title' | 'color'>

export const Header = forwardRef<HTMLElement, HeaderProps>(
  (
    {
      back = false,
      defaultBackPage,
      type,
      left,
      title,
      leftTitle,
      color,
      right,
      position = 'fixed',
      bgcolor,
      responsive = false,
      maxWidth = 600,
      ...props
    },
    ref
  ) => {
    const { router } = useRouter()

    return (
      <AppBar
        position={position}
        {...props}
        sx={{
          width: '100%',
          maxWidth: responsive ? '100%' : maxWidth,
          minWidth: 0,
          margin: '0 auto',
          pt: styles.safeAreaTop,
          left: 0,
          boxShadow: 0,
          backgroundColor: bgcolor || 'background.default',
          // override property backgroundImage of Material-ui to display as designed
          backgroundImage: 'none',
          zIndex: 10,
          ...(props.sx || {}),
        }}
        ref={ref}
      >
        <Toolbar
          disableGutters
          sx={theme => ({
            margin: '0 auto',
            maxWidth: responsive
              ? maxWidth
                ? `${maxWidth - 40}px`
                : '100%'
              : undefined,
            width: 'calc(100% - 40px)',
            mx: responsive ? undefined : 5,
            color: 'text.primary',
            minHeight: 56,
            [theme.breakpoints.up('sm')]: {
              minHeight: 56,
            },
          })}
        >
          <Stack flex={1} direction="row" justifyContent="flex-start">
            {back &&
              (type === 'modal' ? (
                <IconButton.Close
                  onClick={() => router?.goBack(defaultBackPage)}
                  size="medium"
                  sx={{
                    ml: '-8px',
                  }}
                  color={color}
                  bgcolor="transparent"
                />
              ) : (
                <IconButton.Back
                  onClick={() => router?.goBack(defaultBackPage)}
                  size="medium"
                  color={color}
                  bgcolor="transparent"
                  sx={{
                    ml: '-8px',
                  }}
                />
              ))}
            {left}
          </Stack>
          <Stack flex={6} sx={{ minWidth: 0 }}>
            {typeof title === 'string' ? (
              <Typography
                variant="h5"
                sx={styleSets.lineCamp(1)}
                align={leftTitle ? 'left' : 'center'}
                color={color ? 'white' : 'text.primary'}
              >
                {title}
              </Typography>
            ) : (
              title
            )}
          </Stack>
          <Stack flex={1} direction="row" justifyContent="flex-end">
            {right}
          </Stack>
        </Toolbar>
      </AppBar>
    )
  }
)
